import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Wallet, Building2, Users, ShoppingBag, AlertTriangle, PlugZap, Clock, CheckCircle2, RefreshCw, ArrowRight } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { errorText } from '../../lib/errors';
import { auditActionKey, shortAuditPath } from '../../lib/auditActions';
import { PageHeader, Button, StatCard, Card, CardHeader, CardBody, TrendChart, DataTable, StatusBadge, Avatar, LoadingBlock } from '../../ui';
import { ServioStatusTable } from '../../features/servio/ServioStatus';

const growth = (curr, prev) => (prev > 0 ? Math.round(((curr - prev) / prev) * 100) : null);

export default function OverviewPage() {
  const { t, fmt } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/platform/overview');
      setData(res.data);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals;
  const mrrMain = totals?.mrr_by_currency?.UAH ?? 0;
  const mrrOther = Object.entries(totals?.mrr_by_currency ?? {}).filter(([cur]) => cur !== 'UAH');

  // Компании с проблемами — первыми
  const companies = useMemo(() => {
    const rank = (c) => (c.billing_state === 'overdue' ? 0 : c.pos_health === 'error' || c.pos_health === 'degraded' ? 1 : c.pos_health === 'unconfigured' && c.status === 'active' ? 2 : c.billing_state === 'due_soon' ? 3 : 4);
    return [...(data?.companies ?? [])].sort((a, b) => rank(a) - rank(b));
  }, [data]);

  if (loading && !data) return <LoadingBlock />;
  if (!data) return null;

  const alerts = [
    totals.overdue > 0 && { tone: 'danger', icon: AlertTriangle, title: t('overview.alertOverdue', { count: totals.overdue }), text: t('overview.alertOverdueText'), to: '/owner/billing' },
    totals.pos_errors > 0 && { tone: 'danger', icon: PlugZap, title: t('overview.alertPos', { count: totals.pos_errors }), text: t('overview.alertPosText'), to: '/owner/companies?problems=1' },
    totals.due_soon > 0 && { tone: 'warning', icon: Clock, title: t('overview.alertDueSoon', { count: totals.due_soon }), to: '/owner/billing' },
  ].filter(Boolean);

  const columns = [
    {
      key: 'name',
      header: t('companies.company'),
      render: (c) => (
        <div className="flex items-center gap-3">
          <Avatar name={c.name} size="sm" className="rounded-md" />
          <div>
            <p className="font-medium text-zinc-900 dark:text-zinc-100">{c.name}</p>
            <p className="font-mono text-[11px] text-zinc-400">{c.company_code}</p>
          </div>
        </div>
      ),
    },
    { key: 'status', header: t('common.status'), render: (c) => <StatusBadge kind="company" value={c.status} /> },
    { key: 'employees', header: t('companies.employees'), align: 'right', render: (c) => <span className="tabular">{fmt.number(c.employees)}</span> },
    { key: 'tx30_sum', header: t('companies.spend30'), align: 'right', hideOnMobile: true, render: (c) => <span className="tabular">{fmt.money(c.tx30_sum)}</span> },
    { key: 'pos_health', header: t('companies.pos'), render: (c) => <StatusBadge kind="pos" value={c.pos_health} /> },
    { key: 'billing_state', header: t('companies.billing'), hideOnMobile: true, render: (c) => <StatusBadge kind="billing" value={c.billing_state} /> },
  ];

  return (
    <>
      <PageHeader
        title={t('overview.title')}
        description={t('overview.subtitle')}
        actions={<Button variant="secondary" icon={RefreshCw} onClick={load} loading={loading}>{t('common.refresh')}</Button>}
      />

      {/* Что требует внимания */}
      <div className="mb-6 grid gap-3 md:grid-cols-3">
        {alerts.length ? alerts.map((a) => (
          <Link
            key={a.title}
            to={a.to}
            className={`group flex gap-3 rounded-xl border p-4 transition-colors ${a.tone === 'danger'
              ? 'border-red-200 bg-red-50/60 hover:bg-red-50 dark:border-red-900/60 dark:bg-red-950/30'
              : 'border-amber-200 bg-amber-50/60 hover:bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30'}`}
          >
            <a.icon className={`mt-0.5 h-5 w-5 shrink-0 ${a.tone === 'danger' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{a.title}</p>
              {a.text && <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400">{a.text}</p>}
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-zinc-400 transition-transform group-hover:translate-x-0.5" />
          </Link>
        )) : (
          <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 dark:border-emerald-900/60 dark:bg-emerald-950/30 md:col-span-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{t('overview.allGood')}</p>
          </div>
        )}
      </div>

      {/* Ключевые показатели */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('overview.mrr')}
          value={fmt.money(mrrMain)}
          hint={[t('overview.arr', { value: fmt.money(mrrMain * 12) }), ...mrrOther.map(([cur, v]) => `+ ${fmt.money(v, cur)}`)].join(' · ')}
          icon={Wallet}
          tone="brand"
        />
        <StatCard
          label={t('overview.companies')}
          value={fmt.number(totals.companies)}
          hint={t('overview.companiesHint', { active: totals.companies_by_status.active || 0 })}
          icon={Building2}
        />
        <StatCard
          label={t('overview.employees')}
          value={fmt.number(totals.employees)}
          hint={t('overview.employeesHint', { registered: fmt.number(totals.employees_registered), new30: fmt.number(totals.employees_new30) })}
          icon={Users}
        />
        <StatCard
          label={t('overview.spend')}
          value={fmt.money(totals.tx30_sum)}
          delta={growth(totals.tx30_sum, totals.txprev30_sum)}
          hint={growth(totals.tx30_sum, totals.txprev30_sum) !== null ? t('overview.spendHint') : null}
          icon={ShoppingBag}
        />
      </div>

      <div className="mt-6">
        <ServioStatusTable companies={data.companies} onChecked={load} />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader title={t('overview.chartTitle')} description={t('overview.chartSubtitle')} />
          <CardBody>
            <TrendChart
              data={data.trend}
              series={[{ key: 'spend', label: t('overview.seriesSpend') }, { key: 'topup', label: t('overview.seriesTopup') }]}
              formatValue={(v) => fmt.number(v, { notation: 'compact', maximumFractionDigits: 1 })}
              formatDate={(d) => fmt.date(d)}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('overview.activityTitle')}
            actions={<Link to="/owner/audit" className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">{t('overview.allActivity')}</Link>}
          />
          <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {data.recent_audit.length ? data.recent_audit.map((a) => (
              <li key={a.id} className="flex items-start gap-3 px-5 py-3">
                <Avatar name={a.admin_username} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-zinc-800 dark:text-zinc-200">
                    <span className="font-medium">{a.admin_username}</span>{' '}
                    <span className="text-zinc-500">· {auditActionKey(a.method, a.path) ? t(auditActionKey(a.method, a.path)) : t(`audit.methods.${a.method}`)}</span>
                  </p>
                  <p className="truncate text-[11px] text-zinc-400">{a.tenant_name ?? t('audit.platform')} · <span className="font-mono">{shortAuditPath(a.path)}</span></p>
                </div>
                <span className="tabular shrink-0 text-xs text-zinc-400">{fmt.relative(a.created_at)}</span>
              </li>
            )) : <li className="px-5 py-8 text-center text-sm text-zinc-500">{t('audit.empty')}</li>}
          </ul>
        </Card>
      </div>

      <div className="mt-6">
        <div className="mb-3 flex items-end justify-between">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('overview.companiesTitle')}</h2>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('overview.companiesSubtitle')}</p>
          </div>
          <Link to="/owner/companies" className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">{t('overview.allCompanies')}</Link>
        </div>
        <DataTable columns={columns} rows={companies.slice(0, 8)} onRowClick={(c) => navigate(`/owner/companies/${c.id}`)} />
      </div>
    </>
  );
}
